/* crid-wallet.js — rendre visible le VRAI bouton de paiement express (18/08).
 *
 * Le problème, mesuré sur la fiche : `#rpdpNatForm` (le formulaire produit natif dans lequel
 * Shopify monte son bouton wallet) est parqué à `left:-9999px` par `.rpdp-natform`. Le bundle
 * `lys-rpdp.js` ne lui rend sa place que si l'appareil sait faire Apple Pay ET que Shopify
 * recommande `apple_pay`. Or CRID reçoit `recommended.name = "paypal"` : la condition n'est
 * jamais vraie, donc le bouton PayPal express restait invisible sur TOUS les appareils.
 * Le client ne voyait que deux boutons DÉCORATIFS (#rpdpPaypal, #rpdpKlarna) qui se contentent
 * d'aller au checkout générique — d'où « les boutons de paiement fonctionnent pas bien ».
 *
 * Ce que fait ce fichier : il attend qu'un VRAI bouton wallet soit monté, quel qu'il soit
 * (PayPal, Apple Pay, Shop Pay, Google Pay), puis remet le formulaire à sa place et masque le
 * bouton décoratif qui ferait doublon.
 *
 * ⛔ TROIS RÈGLES À NE JAMAIS ENFREINDRE :
 *   1. On ne DÉPLACE pas `<shopify-accelerated-checkout>` dans le DOM : `disconnectedCallback`
 *      démonte sa session de paiement. On ne change que du CSS.
 *   2. On ne le masque pas non plus derrière une couche transparente : Apple refuse d'ouvrir
 *      une session depuis un bouton invisible.
 *   3. Aucun permalink `/cart/<id>:1` nulle part : ça REMPLACE le panier entier.
 *
 * ⚠ Classe `crid-on`, PAS `on` : le bundle minifié continue de piloter `.on` (mode sticky).
 *   Deux classes distinctes = pas de bagarre entre les deux logiques.
 * ⚠ Sans condition Liquid autour du chargement : un script enfermé dans une condition trop
 *   étroite a déjà laissé 54 fiches sans vidéo (28/07). Ce fichier sort de lui-même.
 *
 * Sécurité de facturation : sur CRID le flocage est porté par la VARIANTE
 * (45,99 · 49,99 · 50,99 · 54,99 €), pas par une ligne de supplément. Le paiement express
 * facture donc exactement la variante choisie. Si un jour un supplément en ligne séparée
 * réapparaît, il faudra le réembarquer via `shopify-additional-items[N]` AVANT de rétablir ce
 * bouton, sinon il serait perdu.
 */
(function () {
  'use strict';

  var f = document.getElementById('rpdpNatForm');
  if (!f) return;

  /* Un composant monté rend un custom element de marque. On accepte les quatre, et on exige
     une hauteur réelle : le composant existe dans le DOM bien avant d'avoir un bouton dedans. */
  var MARQUES = {
    'apple-pay-button': 'rpdpApay',
    'shopify-paypal-button': 'rpdpPaypal',
    'shop-pay-button': null,
    'google-pay-button': null,
  };

  /* ⚠ IL FAUT TRAVERSER LES SHADOW DOM. Le bouton PayPal est un enfant direct du composant,
     mais Apple Pay est monté PLUS PROFOND, souvent derrière un `shadowRoot` — un simple
     `querySelector` ne le verrait jamais et le vrai bouton resterait à -9999px.
     Le bundle du thème fait exactement cette descente récursive pour reposer `type="plain"` :
     on reprend la même mécanique, y compris la comparaison par SUFFIXE de nom de balise
     (`shopify-apple-pay-button` finit aussi par `apple-pay-button`). */
  function chercher(n, prof) {
    if (!n || prof > 8) return null;
    var enfants = n.querySelectorAll ? n.querySelectorAll('*') : [];
    for (var i = 0; i < enfants.length; i++) {
      var e = enfants[i], tag = String(e.tagName || '').toLowerCase();
      for (var nom in MARQUES) {
        /* `left:-9999px` ne change pas la HAUTEUR : la mesure reste valable hors écran. */
        if (tag === nom || tag.slice(-nom.length - 1) === '-' + nom) {
          if (e.getBoundingClientRect().height > 10) return nom;
        }
      }
      if (e.shadowRoot) { var r = chercher(e.shadowRoot, prof + 1); if (r) return r; }
    }
    return null;
  }

  function monte() {
    var ac = f.querySelector('shopify-accelerated-checkout, shopify-accelerated-checkout-cart');
    return ac ? chercher(ac, 0) : null;
  }

  /* ══ ⛔ POINT D'ARGENT — la variante du formulaire natif ═══════════════════════════════════
     Mesuré le 18/08 : en choisissant « Name + number », le prix affiché passait bien de 45,99 à
     54,99 €, mais `#rpdpNatVid` restait sur la variante de départ. Le bouton express aurait donc
     facturé 45,99 € et expédié un maillot SANS flocage. Le bug était invisible tant que le bouton
     était caché — le rendre visible l'aurait mis en production.
     Source de vérité : `#rpdpFormVid`, mis à jour par la section (comme `?variant=` dans l'URL).
     ⚠ Une affectation `input.value = …` en JS ne déclenche NI événement `change` NI mutation
       d'attribut : ni `addEventListener` ni `MutationObserver` ne verraient rien. On compare la
       valeur, c'est le seul moyen fiable ici. */
  var src = document.getElementById('rpdpFormVid');
  var nat = document.getElementById('rpdpNatVid');


  var dernier = null;
  function synchro() {
    if (!src || !src.value || src.value === dernier) return;
    dernier = src.value;
    if (nat) nat.value = src.value;
    /* 12/09 : le libellé Klarna est neutre, sans montant « 4 × » : il ne suit plus le prix. */
  }
  synchro();
  setInterval(synchro, 250);

  /* ══ ⛔ LE BOUTON APPLE PAY DÉCORATIF EST RETIRÉ (18/08) ═══════════════════════════════════
     Retour du patron : « le bouton Apple Pay ne marche pas sur mon iPhone ». Mesuré en variant
     l'agent utilisateur, l'attribut `recommended` écrit par Shopify vaut :
         Chrome PC → paypal · Chrome Android → paypal
         Safari iPhone → shop_pay · Safari macOS → shop_pay
     Shopify ne monte qu'UN wallet et, sur Apple, il choisit **Shop Pay**. Il n'y a donc jamais
     de vrai bouton Apple Pay sur la fiche. Ce que le patron tapait était `#rpdpApay` : un bouton
     MAISON portant le logo Apple Pay, qui se contente d'ouvrir le checkout.
     Deux raisons de le retirer, pas une :
       · il ment au client — il a l'apparence d'Apple Pay et n'ouvre aucune feuille Apple Pay ;
       · Apple INTERDIT d'apposer sa marque sur un bouton qui n'ouvre pas de session Apple Pay.
     Et on ne peut pas le réparer par du code : « on ne fabrique pas un bouton Apple Pay à partir
     d'un autre wallet » (les `wallet_params` de shop_pay n'ont ni countryCode, ni
     merchantCapabilities, ni supportedNetworks → TypeError, feuille jamais ouverte).
     Pour obtenir un VRAI bouton Apple Pay, il faut désactiver Shop Pay dans les réglages de
     paiement — décision du patron. Ce fichier est prêt pour ce jour-là :
     `apple-pay-button` est déjà dans MARQUES, le vrai bouton prendra la place tout seul. */
  var faux = document.getElementById('rpdpApay');
  if (faux) faux.style.display = 'none';

  /* ⚠ LE MÊME FAUX BOUTON EXISTE DANS LA BARRE COLLANTE (`#rpdpStickyPay`).
     On ne peut PAS le masquer tout de suite : le bundle s'en sert de GABARIT pour placer le vrai
     bouton Apple Pay (il mesure son rectangle, puis lui met `.slotted` qui le cache sans lui
     retirer sa place). Le masquer en `display:none` donnerait un rectangle nul et casserait ce
     placement.
     On laisse donc au bundle le temps de s'armer, et on ne masque QUE s'il ne l'a pas fait :
     dans ce cas le vrai bouton n'ira jamais dans la barre, et ce qui reste est un bouton maison
     au logo Apple Pay qui n'ouvre aucune feuille — ce qu'Apple interdit. */
  setTimeout(function () {
    var st = document.getElementById('rpdpStickyPay');
    if (!st) return;
    var arme = f.classList.contains('on') || f.dataset.armed === '1';
    if (!arme) st.style.display = 'none';
  }, 9000);

  var essais = 0;
  var t = setInterval(function () {
    var nom = monte();
    if (nom) {
      clearInterval(t);
      f.classList.add('crid-on');
      var doublon = MARQUES[nom] && document.getElementById(MARQUES[nom]);
      if (doublon) doublon.style.display = 'none';
      try { document.documentElement.setAttribute('data-crid-wallet', nom); } catch (e) { /* rien */ }
      return;
    }
    if (++essais > 48) {
      clearInterval(t);
      /* Aucun wallet monté au bout de 12 s : le créneau express ne doit pas rester VIDE.
         On force le bouton PayPal de repli, que le CSS masque sur les appareils Apple
         (règle `html.rpdp-noap`) — or ici il n'y a plus de bouton Apple Pay à protéger.
         Le mode dégradé est l'ancien comportement, jamais « rien ». */
      var repli = document.getElementById('rpdpPaypal');
      if (repli) repli.style.display = 'flex';
    }
  }, 250);
})();
